import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "./en.json";
import { isDevelopment } from "~/helpers/environment";

const REMOTE_TRANSLATIONS_URL =
  "https://dropshiptool-production-frontend-app.s3.us-east-1.amazonaws.com";
const MAIN_LANGUAGE = "en";

/**
 * Languages that actually have an `en (<code>).json` file on S3. Must stay in
 * sync with `locale_map` in .github/scripts/sync-crowdin-translations.sh and
 * LANGUAGE_CODES in src/components/language-dropdown/index.tsx.
 *
 * Requesting any other code gets a 403 from S3 (the object does not exist);
 * bare base codes like "pt" never have a file either — only "pt-BR"/"pt-PT"
 * are exported. Unknown codes are skipped entirely so the UI falls back to
 * English instead of spinning on a request that can never succeed.
 */
export const REMOTE_LANGUAGES = [
  "cs",
  "da",
  "de",
  "es",
  "fi",
  "fr",
  "it",
  "ja",
  "ko",
  "nb",
  "nl",
  "pl",
  "pt-BR",
  "pt-PT",
  "sv",
  "th",
  "tr",
  "zh-CN",
  "zh-TW",
];

/**
 * Returns the S3 URL for a supported remote language, or undefined for English
 * (bundled) and for codes with no file on S3. i18next-http-backend treats an
 * empty load path as "no resources" without erroring, so unsupported locales
 * resolve immediately to the English fallback.
 */
export const getRemoteLoadPath = (
  lngs: string[] | string
): string | undefined => {
  const lng = Array.isArray(lngs) ? lngs[0] : lngs;
  if (lng === MAIN_LANGUAGE || !REMOTE_LANGUAGES.includes(lng)) {
    return undefined;
  }
  return `${REMOTE_TRANSLATIONS_URL}/src/translations/en (${lng}).json`;
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: isDevelopment,
    lng: MAIN_LANGUAGE,
    fallbackLng: MAIN_LANGUAGE,
    // Load only the exact requested code: resolving "pt-BR" must not also
    // request a bare "pt" file that does not exist on S3.
    load: "currentOnly",
    // English ships in the bundle and is registered synchronously below; the
    // HTTP backend is only consulted for the other languages.
    partialBundledLanguages: true,
    resources: {
      en: {
        translation: {
          ...en,
        },
      },
    },
    interpolation: {
      escapeValue: false,
    },
    returnNull: false,
    returnEmptyString: false,
    react: {
      // Never suspend the tree on translation loading. A missing or failing
      // remote file previously left every route stuck on the Suspense
      // "Loading..." fallback (Spanish/Arabic signup, QA 14 Oct); with
      // suspense off the UI renders in English until the file arrives.
      useSuspense: false,
    },
    backend: {
      loadPath: getRemoteLoadPath,
      allowMultiLoading: false,
      crossDomain: true,
    },
  });

export default i18n;
